// FASE 2.1 — Price Alerts page (/shadow/alertas).
// Authenticated users (FREE + PRO) create alerts; backend cron evaluates via CoinGecko
// and creates an in-app notification when the price crosses the target.
import React, { useEffect, useState } from "react";
import { Bell, TrendingUp, TrendingDown, Trash2, Loader2, Plus, AlertCircle, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { API, safeJson } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../i18n/LanguageContext";
import { tFases } from "../i18n/shadow-fases";

const SUPPORTED_TICKERS = ["BTC", "ETH", "BNB", "SOL", "XRP", "ADA", "DOGE", "AVAX", "DOT", "MATIC", "LINK", "ATOM", "ARB", "OP", "LTC", "TRX", "SHIB", "TON", "NEAR", "APT", "SUI", "BCH", "XLM", "ETC", "FIL", "ALGO", "AAVE", "UNI"];

export default function ShadowAlertsPage() {
  const { user, token, openLogin } = useAuth();
  const { lang } = useLanguage();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({ ticker: "BTC", condition: "above", target_price: "" });
  const [livePrice, setLivePrice] = useState(null);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  // Load alerts
  useEffect(() => {
    let alive = true;
    const load = async () => {
      if (!user) { if (alive) setLoading(false); return; }
      try {
        const res = await fetch(`${API}/shadow/alerts`, {
          credentials: "include",
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const data = await safeJson(res);
        if (alive && res.ok && data) setAlerts(data.alerts || []);
      } finally {
        if (alive) setLoading(false);
      }
    };
    load();
    return () => { alive = false; };
  }, [user, token]);

  // Live price preview for the selected ticker
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const res = await fetch(`${API}/shadow/alerts/price/${form.ticker}`);
        const data = await safeJson(res);
        if (!cancelled && res.ok && data) setLivePrice(data.price);
        else if (!cancelled) setLivePrice(null);
      } catch {
        if (!cancelled) setLivePrice(null);
      }
    };
    run();
    const t = setInterval(run, 20000);
    return () => { cancelled = true; clearInterval(t); };
  }, [form.ticker]);

  const create = async (e) => {
    e?.preventDefault?.();
    if (creating) return;
    setError(null);
    const target = parseFloat(form.target_price);
    if (!isFinite(target) || target <= 0) { setError(tFases(lang, "al_invalid_target")); return; }
    setCreating(true);
    try {
      const res = await fetch(`${API}/shadow/alerts`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        credentials: "include",
        body: JSON.stringify({ ticker: form.ticker, condition: form.condition, target_price: target }),
      });
      const data = await safeJson(res);
      if (!res.ok) {
        const msg = data?.detail?.message || data?.detail || "Error creando alerta";
        throw new Error(typeof msg === "string" ? msg : "Error creando alerta");
      }
      setAlerts([data.alert, ...alerts]);
      setForm({ ...form, target_price: "" });
    } catch (err) {
      setError(err.message);
    } finally {
      setCreating(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm(tFases(lang, "al_confirm_delete"))) return;
    try {
      const res = await fetch(`${API}/shadow/alerts/${id}`, {
        method: "DELETE",
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (res.ok) setAlerts(alerts.filter((a) => a.id !== id));
    } catch (_e) { /* non-critical */ }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-black text-white">
        <Navbar />
        <section className="mx-auto max-w-2xl px-6 pt-40 pb-24 text-center">
          <Bell className="mx-auto h-10 w-10 text-amber-300/80" />
          <h1 className="mt-4 text-3xl font-black tracking-tight">{tFases(lang, "al_title")}</h1>
          <p className="mt-2 text-sm text-white/55">
            {tFases(lang, "al_login_sub")}
          </p>
          <button
            onClick={openLogin}
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-emerald-400 px-6 py-3 text-xs font-black uppercase tracking-[0.22em] text-black hover:-translate-y-0.5 hover:shadow-[0_0_40px_rgba(52,211,153,0.55)] transition-all"
            data-testid="alerts-login-btn"
          >
            {tFases(lang, "common_login")}
          </button>
        </section>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white" data-testid="shadow-alerts-page">
      <Navbar />

      <section className="mx-auto max-w-4xl px-6 pt-32 pb-24">
        <Link
          to="/shadow"
          className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.22em] text-white/40 transition-colors hover:text-emerald-300"
          data-testid="alerts-back-link"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> {tFases(lang, "common_back")}
        </Link>

        <div className="mt-6">
          <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-amber-300/80">
            // FASE 2 · ALERTAS
          </p>
          <h1 className="mt-2 font-display text-4xl font-black tracking-tight md:text-5xl">
            {tFases(lang, "al_title")}
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-white/55">
            {tFases(lang, "al_sub")}
          </p>
        </div>

        {/* Create form */}
        <form
          onSubmit={create}
          className="mt-8 rounded-2xl border border-amber-400/20 bg-amber-400/[0.03] p-5 backdrop-blur"
          data-testid="alert-create-form"
        >
          <div className="grid grid-cols-1 gap-3 md:grid-cols-[1fr_auto_1fr_auto]">
            <div>
              <label className="text-[10px] font-bold uppercase tracking-[0.22em] text-white/45">{tFases(lang, "al_ticker")}</label>
              <select
                value={form.ticker}
                onChange={(e) => setForm({ ...form, ticker: e.target.value })}
                className="mt-1.5 w-full rounded-xl border border-white/10 bg-black/40 px-3 py-2.5 text-sm outline-none focus:border-amber-400/50"
                data-testid="alert-ticker-select"
              >
                {SUPPORTED_TICKERS.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-[0.22em] text-white/45">{tFases(lang, "al_when")}</label>
              <div className="mt-1.5 flex gap-1">
                {[
                  { id: "above", label: "Sube por encima", icon: TrendingUp, color: "#10b981" },
                  { id: "below", label: "Baja por debajo", icon: TrendingDown, color: "#ef4444" },
                ].map((c) => (
                  <button
                    type="button"
                    key={c.id}
                    onClick={() => setForm({ ...form, condition: c.id })}
                    data-testid={`alert-cond-${c.id}`}
                    className="inline-flex items-center gap-1.5 rounded-xl border px-3 py-2.5 text-[11px] font-bold uppercase tracking-wider transition-all"
                    style={{
                      borderColor: form.condition === c.id ? c.color : "rgba(255,255,255,0.1)",
                      background: form.condition === c.id ? `${c.color}1f` : "transparent",
                      color: form.condition === c.id ? c.color : "rgba(255,255,255,0.55)",
                    }}
                  >
                    <c.icon className="h-3.5 w-3.5" />
                    {c.id === "above" ? "↑" : "↓"}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-[0.22em] text-white/45">
                {tFases(lang, "al_target")}
                {livePrice && <span className="ml-2 font-mono text-amber-300/80">· {tFases(lang, "al_spot")} ${livePrice.toLocaleString()}</span>}
              </label>
              <input
                type="number"
                step="any"
                value={form.target_price}
                onChange={(e) => setForm({ ...form, target_price: e.target.value })}
                placeholder={livePrice ? String(Math.round(livePrice * (form.condition === "above" ? 1.05 : 0.95))) : "100000"}
                className="mt-1.5 w-full rounded-xl border border-white/10 bg-black/40 px-3 py-2.5 text-sm outline-none focus:border-amber-400/50"
                data-testid="alert-target-input"
              />
            </div>
            <div className="flex items-end">
              <button
                type="submit"
                disabled={creating}
                className="inline-flex h-[42px] items-center justify-center gap-1.5 rounded-xl bg-gradient-to-r from-amber-400 to-amber-500 px-5 text-[11px] font-black uppercase tracking-[0.16em] text-black transition-all hover:-translate-y-0.5 disabled:cursor-wait disabled:opacity-60"
                data-testid="alert-submit"
              >
                {creating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
                {tFases(lang, "al_create")}
              </button>
            </div>
          </div>
          {error && (
            <div className="mt-3 flex items-center gap-2 rounded-xl border border-rose-500/40 bg-rose-500/[0.08] px-3 py-2 text-xs text-rose-200" data-testid="alert-error">
              <AlertCircle className="h-3.5 w-3.5" />
              {error}
            </div>
          )}
        </form>

        {/* Active alerts */}
        <div className="mt-8">
          <h2 className="text-sm font-bold uppercase tracking-[0.2em] text-white/55">
            {tFases(lang, "al_my", { n: alerts.length })}
          </h2>
          {loading ? (
            <div className="mt-4 flex items-center gap-2 text-white/45">
              <Loader2 className="h-4 w-4 animate-spin" /> {tFases(lang, "common_loading")}
            </div>
          ) : alerts.length === 0 ? (
            <div className="mt-4 rounded-2xl border border-white/10 bg-white/[0.025] p-10 text-center backdrop-blur">
              <Bell className="mx-auto h-9 w-9 text-white/35" />
              <p className="mt-3 text-sm text-white/55">{tFases(lang, "al_empty")}</p>
            </div>
          ) : (
            <ul className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
              {alerts.map((a) => {
                const cond = a.condition === "above";
                const Icon = cond ? TrendingUp : TrendingDown;
                return (
                  <li
                    key={a.id}
                    className={`relative overflow-hidden rounded-2xl border p-4 backdrop-blur ${
                      a.triggered
                        ? "border-emerald-400/40 bg-emerald-400/[0.04]"
                        : "border-white/10 bg-white/[0.025]"
                    }`}
                    data-testid={`alert-row-${a.id}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-base font-black text-white">{a.ticker}</span>
                          <span
                            className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[9px] font-black uppercase tracking-wider"
                            style={{
                              borderColor: cond ? "rgba(16,185,129,0.4)" : "rgba(239,68,68,0.4)",
                              color: cond ? "#34d399" : "#f87171",
                              background: cond ? "rgba(16,185,129,0.08)" : "rgba(239,68,68,0.08)",
                            }}
                          >
                            <Icon className="h-3 w-3" />
                            {cond ? tFases(lang, "al_above") : tFases(lang, "al_below")} ${Number(a.target_price).toLocaleString()}
                          </span>
                          {a.triggered && (
                            <span className="rounded-full border border-emerald-400/50 bg-emerald-400/15 px-2 py-0.5 text-[9px] font-black uppercase tracking-wider text-emerald-200">
                              {tFases(lang, "al_triggered")}
                            </span>
                          )}
                        </div>
                        <p className="mt-1.5 text-[11px] text-white/45">
                          {a.last_price != null && (
                            <>{tFases(lang, "al_last_spot")}: <span className="font-mono text-white/70">${Number(a.last_price).toLocaleString()}</span> · </>
                          )}
                          {a.triggered_at
                            ? `${tFases(lang, "al_triggered_at")} ${new Date(a.triggered_at).toLocaleString()}`
                            : `${tFases(lang, "al_created_at")} ${new Date(a.created_at).toLocaleString()}`}
                        </p>
                      </div>
                      <button
                        onClick={() => remove(a.id)}
                        className="rounded-full border border-white/10 p-1.5 text-white/45 transition-colors hover:border-rose-400/40 hover:text-rose-300"
                        aria-label="Eliminar"
                        data-testid={`alert-delete-${a.id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
